import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { btnSecondary, FieldError, inputCls } from "@/components/admin/AdminLayout";

const iconBtn = "h-11 w-11 shrink-0 border border-zinc-700 bg-transparent p-0 text-zinc-200 hover:bg-zinc-800 disabled:opacity-30";

export const ContactsEditor = ({ contacts, onChange, error }) => {
  const set = (i, k, v) => onChange(contacts.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const move = (i, d) => {
    const next = [...contacts];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <div data-testid="contacts-editor">
      <p className="mb-3 text-sm text-zinc-400">The first contact is the main “Call Now” number.</p>
      <div className="space-y-3">
        {contacts.map((c, i) => (
          <div key={i} className="rounded-md border border-zinc-800 bg-[#121214] p-3" data-testid={`contact-row-${i}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-sub text-sm uppercase tracking-wider text-zinc-400">{i === 0 ? "Main contact" : `Contact ${i + 1}`}</span>
              <div className="flex gap-2">
                <Button type="button" className={iconBtn} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`contact-move-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button type="button" className={iconBtn} disabled={i === contacts.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`contact-move-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                <Button type="button" className={iconBtn} onClick={() => onChange(contacts.filter((_, j) => j !== i))} aria-label="Remove contact" data-testid={`contact-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Input placeholder="Name" value={c.name} onChange={(e) => set(i, "name", e.target.value)} className={inputCls} data-testid={`contact-name-input-${i}`} />
              <Input placeholder="Title (e.g. Co-Founder)" value={c.title} onChange={(e) => set(i, "title", e.target.value)} className={inputCls} data-testid={`contact-title-input-${i}`} />
              <Input placeholder="Phone" type="tel" value={c.phone} onChange={(e) => set(i, "phone", e.target.value)} className={inputCls} data-testid={`contact-phone-input-${i}`} />
            </div>
          </div>
        ))}
      </div>
      <FieldError msg={error} testId="contacts-error" />
      <Button type="button" className={`${btnSecondary} mt-3`} onClick={() => onChange([...contacts, { name: "", title: "", phone: "" }])} data-testid="contact-add-button">
        <Plus className="h-4 w-4" /> Add contact
      </Button>
    </div>
  );
};

export const HoursEditor = ({ mode, hours, onMode, onHours }) => {
  const set = (i, k, v) => onHours(hours.map((h, j) => (j === i ? { ...h, [k]: v } : h)));
  const opt = (value, label) => (
    <button
      type="button"
      onClick={() => onMode(value)}
      className={`h-12 flex-1 rounded-md border text-base font-medium transition-colors ${mode === value ? "border-[#FFD23F] bg-[#FFD23F] text-[#121214]" : "border-zinc-700 text-zinc-200 hover:bg-zinc-800"}`}
      data-testid={`hours-mode-${value}`}
    >
      {label}
    </button>
  );
  return (
    <div data-testid="hours-editor">
      <div className="flex gap-2">{opt("appointment", "By appointment")}{opt("set", "Set hours")}</div>
      {mode === "set" && (
        <div className="mt-4 space-y-2">
          {hours.map((h, i) => (
            <div key={h.day} className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-[#121214] p-2" data-testid={`hours-day-${h.day.toLowerCase()}`}>
              <span className="w-24 text-sm font-medium text-zinc-200">{h.day}</span>
              <label className="flex h-11 items-center gap-2 px-1 text-sm text-zinc-300">
                <Checkbox checked={h.closed} onCheckedChange={(v) => set(i, "closed", !!v)} data-testid={`hours-closed-${h.day.toLowerCase()}`} /> Closed
              </label>
              {!h.closed && (
                <div className="flex flex-1 items-center gap-2">
                  <Input type="time" value={h.open} onChange={(e) => set(i, "open", e.target.value)} className={`${inputCls} min-w-0 flex-1`} data-testid={`hours-open-${h.day.toLowerCase()}`} />
                  <span className="text-zinc-500">to</span>
                  <Input type="time" value={h.close} onChange={(e) => set(i, "close", e.target.value)} className={`${inputCls} min-w-0 flex-1`} data-testid={`hours-close-${h.day.toLowerCase()}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
